import { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Badge } from "../components/ui/badge";
import { Checkbox } from "../components/ui/checkbox";
import { useAuth } from "../App";
import { toast } from "sonner";
import {
  Building2, ArrowLeft, User, Mail, Phone, MapPin, Save, Loader2,
  Sparkles, Leaf, Waves, Wind, Wrench, Star, CheckCircle
} from "lucide-react";

const API_URL = process.env.REACT_APP_BACKEND_URL;

const CATEGORIES = [
  { id: "cat_diarista", name: "Diarista", icon: Sparkles },
  { id: "cat_jardinagem", name: "Jardinagem", icon: Leaf },
  { id: "cat_piscina", name: "Piscina", icon: Waves },
  { id: "cat_arcondicionado", name: "Ar-Condicionado", icon: Wind },
  { id: "cat_reforma", name: "Reforma Leve", icon: Wrench }
];

export default function ProfilePage() {
  const { user, token, setUser } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [provider, setProvider] = useState(null);
  const [formData, setFormData] = useState({
    name: user?.name || "",
    phone: user?.phone || "",
    categories: [],
    description: "",
    service_radius_km: 20,
    hourly_rate: "",
    experience_years: 0
  });

  useEffect(() => {
    if (user?.role === "provider") {
      fetchProviderProfile();
    }
  }, [user]);

  const fetchProviderProfile = async () => {
    try {
      const response = await fetch(`${API_URL}/api/providers`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.ok) {
        const providers = await response.json();
        const myProvider = providers.find(p => p.user_id === user?.user_id);
        if (myProvider) {
          setProvider(myProvider);
          setFormData(prev => ({
            ...prev,
            categories: myProvider.categories || [],
            description: myProvider.description || "",
            service_radius_km: myProvider.service_radius_km || 20,
            hourly_rate: myProvider.hourly_rate?.toString() || "",
            experience_years: myProvider.experience_years || 0
          }));
        }
      }
    } catch (error) {
      console.error("Error fetching provider profile:", error);
    }
  };

  const handleCategoryToggle = (categoryId) => {
    setFormData(prev => ({
      ...prev,
      categories: prev.categories.includes(categoryId)
        ? prev.categories.filter(c => c !== categoryId)
        : [...prev.categories, categoryId]
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      if (user?.role === "provider") {
        const response = await fetch(`${API_URL}/api/providers/profile`, {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`
          },
          body: JSON.stringify({
            categories: formData.categories,
            description: formData.description,
            service_radius_km: parseInt(formData.service_radius_km),
            hourly_rate: formData.hourly_rate ? parseFloat(formData.hourly_rate) : null,
            experience_years: parseInt(formData.experience_years)
          })
        });

        if (!response.ok) {
          throw new Error("Erro ao atualizar perfil");
        }

        const updated = await response.json();
        setProvider(updated);
        toast.success("Perfil atualizado com sucesso!");
      }
    } catch (error) {
      toast.error(error.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-stone-50" data-testid="profile-page">
      {/* Header */}
      <header className="bg-white border-b border-slate-200 sticky top-0 z-40">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-4">
              <Button 
                variant="ghost" 
                size="icon" 
                onClick={() => navigate(user?.role === "provider" ? "/provider" : "/client")}
                data-testid="back-btn"
              >
                <ArrowLeft className="w-5 h-5" />
              </Button>
              <Link to="/" className="flex items-center gap-2">
                <div className="w-10 h-10 bg-slate-900 rounded-lg flex items-center justify-center">
                  <Building2 className="w-6 h-6 text-white" />
                </div>
              </Link>
              <span className="font-semibold text-slate-900">Meu Perfil</span>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Profile Header */}
        <Card className="mb-6">
          <CardContent className="p-6">
            <div className="flex items-center gap-6">
              <div className="w-20 h-20 bg-slate-200 rounded-full flex items-center justify-center overflow-hidden">
                {user?.picture ? (
                  <img src={user.picture} alt={user.name} className="w-full h-full object-cover" />
                ) : (
                  <User className="w-10 h-10 text-slate-500" />
                )}
              </div>
              <div className="flex-1">
                <div className="flex items-center gap-2">
                  <h1 className="text-2xl font-bold text-slate-900">{user?.name}</h1>
                  {user?.role === "provider" && provider?.is_verified && (
                    <CheckCircle className="w-5 h-5 text-emerald-500" />
                  )}
                </div>
                <p className="text-slate-500">{user?.email}</p>
                <Badge className="mt-2" variant={user?.role === "provider" ? "default" : "secondary"}>
                  {user?.role === "provider" ? "Prestador" : "Cliente"}
                </Badge>
              </div>
              {user?.role === "provider" && provider && (
                <div className="text-right">
                  <div className="flex items-center gap-1 justify-end">
                    <Star className="w-5 h-5 text-amber-400 fill-amber-400" />
                    <span className="text-xl font-bold">{provider.average_rating?.toFixed(1) || "0.0"}</span>
                  </div>
                  <p className="text-sm text-slate-500">{provider.total_reviews || 0} avaliações</p>
                </div>
              )}
            </div>
          </CardContent>
        </Card>

        <form onSubmit={handleSubmit}>
          {/* Basic Info */}
          <Card className="mb-6">
            <CardHeader>
              <CardTitle>Informações Básicas</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid sm:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="name">Nome</Label>
                  <Input
                    id="name"
                    value={formData.name}
                    onChange={(e) => setFormData({...formData, name: e.target.value})}
                    placeholder="Seu nome"
                    disabled
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="phone">Telefone</Label>
                  <Input
                    id="phone"
                    value={formData.phone}
                    onChange={(e) => setFormData({...formData, phone: e.target.value})}
                    placeholder="(11) 99999-9999"
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="email">Email</Label>
                <Input
                  id="email"
                  value={user?.email}
                  disabled
                  className="bg-slate-50"
                />
              </div>
            </CardContent>
          </Card>

          {/* Provider Settings */}
          {user?.role === "provider" && (
            <>
              <Card className="mb-6">
                <CardHeader>
                  <CardTitle>Categorias de Serviço</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-sm text-slate-500 mb-4">
                    Selecione as categorias de serviço que você oferece
                  </p>
                  <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
                    {CATEGORIES.map((category) => {
                      const Icon = category.icon;
                      const isSelected = formData.categories.includes(category.id);
                      return (
                        <div
                          key={category.id}
                          onClick={() => handleCategoryToggle(category.id)}
                          className={`flex items-center gap-3 p-4 rounded-lg border-2 cursor-pointer transition-all ${
                            isSelected 
                              ? "border-emerald-500 bg-emerald-50" 
                              : "border-slate-200 hover:border-slate-300"
                          }`}
                          data-testid={`category-${category.id}`}
                        >
                          <Checkbox checked={isSelected} onChange={() => {}} />
                          <Icon className={`w-5 h-5 ${isSelected ? "text-emerald-600" : "text-slate-500"}`} />
                          <span className={`font-medium ${isSelected ? "text-emerald-700" : "text-slate-700"}`}>
                            {category.name}
                          </span>
                        </div>
                      );
                    })}
                  </div>
                </CardContent>
              </Card>

              <Card className="mb-6">
                <CardHeader>
                  <CardTitle>Detalhes do Serviço</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="description">Descrição do seu trabalho</Label>
                    <Textarea
                      id="description"
                      value={formData.description}
                      onChange={(e) => setFormData({...formData, description: e.target.value})}
                      placeholder="Descreva seus serviços, experiência e diferenciais..."
                      rows={4}
                    />
                  </div>
                  <div className="grid sm:grid-cols-3 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="hourly_rate">Valor/Hora (R$)</Label>
                      <Input
                        id="hourly_rate"
                        type="number"
                        value={formData.hourly_rate}
                        onChange={(e) => setFormData({...formData, hourly_rate: e.target.value})}
                        placeholder="Ex: 50"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="experience">Anos de Experiência</Label>
                      <Input
                        id="experience"
                        type="number"
                        value={formData.experience_years}
                        onChange={(e) => setFormData({...formData, experience_years: e.target.value})}
                        placeholder="Ex: 5"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="radius">Raio de Atendimento (km)</Label>
                      <Input
                        id="radius"
                        type="number"
                        value={formData.service_radius_km}
                        onChange={(e) => setFormData({...formData, service_radius_km: e.target.value})}
                        placeholder="Ex: 20"
                      />
                    </div>
                  </div>
                </CardContent>
              </Card>
            </>
          )}

          {/* Save Button */}
          <div className="flex justify-end">
            <Button 
              type="submit" 
              className="bg-slate-900 hover:bg-slate-800"
              disabled={loading}
              data-testid="save-profile-btn"
            >
              {loading ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Salvando...
                </>
              ) : (
                <>
                  <Save className="w-4 h-4 mr-2" />
                  Salvar Alterações
                </>
              )}
            </Button>
          </div>
        </form>
      </main>
    </div>
  );
}
